<template>
  <master-layout pageTitle="SSO Settings">
    <ion-content class="provider-setup-page">
      <!-- Header -->
      <ion-header class="ion-no-border">
        <ion-toolbar>
          <ion-buttons slot="start">
            <ion-button @click="goBack">
              <VIcon name="arrow-left-line" :size="-4" class="backBttn cursorPointer"></VIcon>
            </ion-button>
          </ion-buttons>
          <ion-title>View Tenant and SSO provider</ion-title>
        </ion-toolbar>
      </ion-header>

      <!-- Tenant configuration -->
      <div class="page-content" v-if="!addNewTenant">
        <div class="provider-type-section">
          <div class="section-header">
            <h2>Tenant</h2>
            <p>Configure Tenant</p>
          </div>

          <div class="tenant-input" v-if="loadingTenants">
            <ion-spinner name="crescent" class="loading-spinner"></ion-spinner>
            <p>Loading Tenant...</p>
          </div>
          <div
            class="provider-dropdown form-row"
            v-else-if="tenants.length > 0"
          >
            <div class="form-field">
              <label>Select Tenant *</label>
              <ion-select
                v-model="selectedTenant"
                interface="popover"
                placeholder="Select a tenant"
              >
                <ion-select-option
                  v-for="tenant in tenants"
                  :key="tenant.tenant_id"
                  :value="tenant"
                >
                  <div class="select-option">
                    <div class="option-sub">{{ tenant.display_name }}</div>
                  </div>
                </ion-select-option>
              </ion-select>
            </div>

            <div class="tenant-details" v-if="selectedTenant">
              <ion-label>Tenant Details</ion-label>
              <div class="selected-tenant-details">
                <div class="selected-tenant-header">
                  <span>Tenant Name</span>
                  <span>Tenant Id</span>
                  <span>Action</span>
                </div>
                <div class="selected-tenant border">
                  <span>{{ selectedTenant.display_name }}</span>
                  <span>{{ selectedTenant.tenant_id }}</span>
                  <ion-button
                    @click="confirmDeleteTenant"
                    fill="clear"
                    color="danger"
                    class="delete-tenant-btn"
                    ><VIcon name="delete-line" connotation="alert"></VIcon>
                  </ion-button>
                </div>
              </div>
            </div>
          </div>

          <div class="tenant-input" v-else>
            <p>No tenant available. Please create a tenant to proceed.</p>
            <div class="add-tenant-actions">
              <ion-button
                fill="solid"
                @click="addNewTenant = true"
                class="add-tenant-btn"
                ><ion-label>Add New Tenant</ion-label></ion-button
              >
              <ion-button
                class="cancel-btn"
                fill="outline"
                @click="goBack"
                :disabled="loading"
              >
                <ion-label>Cancel</ion-label>
              </ion-button>
            </div>
          </div>
        </div>
      </div>

      <div class="page-content" v-else>
        <div class="provider-type-section">
          <div class="section-header">
            <h2>Add tenant</h2>
            <p>
              Each tenant contains its own identity providers, settings and sets
              of users. Tenants will be available via the API and SDK
            </p>
          </div>

          <div class="tenant-input">
            <div class="form-field full-width">
              <label>Tenant Display Name *</label>
              <ion-input
                v-model="tenantName"
                placeholder="Add tenant name"
                class="full-width"
                autocomplete
              ></ion-input>
            </div>

            <div class="add-tenant-actions">
              <ion-button
                class="next-btn"
                fill="solid"
                @click="createTenant"
                :disabled="loading || !tenantName"
              >
                <ion-label>Save</ion-label>
              </ion-button>
              <ion-button
                class="cancel-btn"
                fill="outline"
                @click="addNewTenant = false"
                :disabled="loading"
              >
                <ion-label>Cancel</ion-label>
              </ion-button>
            </div>
          </div>
        </div>
      </div>

      <div class="page-content" v-if="tenantId || selectedTenant?.tenant_id">
        <!-- Provider Type Selection -->
        <div class="provider-type-section" v-if="!loading || !selectedProvider">
          <div class="section-header">
            <h2>Configure SSO Provider</h2>
            <p>Configure the Identity provider</p>
          </div>

          <div class="provider-dropdown">
            <div class="form-field">
              <label>Provider Type *</label>
              <ion-select
                :disabled="providerExists"
                v-model="selectedProvider"
                interface="popover"
                placeholder="Select a provider type"
                @selectionChange="onProviderTypeChange"
              >
                <ion-select-option value="saml">
                  <div class="select-option">
                    <div class="option-sub">SAML</div>
                  </div>
                </ion-select-option>
                <ion-select-option value="oidc">
                  <div class="select-option">
                    <div class="option-sub">OIDC</div>
                  </div>
                </ion-select-option>
              </ion-select>
            </div>
          </div>
        </div>

        <!-- Configuration Form - appears after provider selection -->
        <div v-if="selectedProvider" class="configuration-section">
          <div class="section-header">
            <h2>Configure {{ selectedProvider.toUpperCase() }} provider</h2>
            <p>
              Enter the configuration details for your
              {{ selectedProvider.toUpperCase() }} Identity provider
            </p>
          </div>

          <form @submit.prevent="saveProvider" class="config-form">
            <!-- Basic Information -->
            <div class="form-section">
              <h3>Basic Information</h3>

              <div class="form-row">
                <div class="form-field">
                  <label>Name *</label>
                  <ion-input
                    v-model="providerConfig.name"
                    placeholder="e.g., Corporate SSO"
                    required
                    autocomplete
                  ></ion-input>
                  <small>A friendly name for this provider</small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>Provider ID</label>
                  <ion-input
                    v-model="providerConfig.providerId"
                    placeholder="e.g., corporate-sso"
                    required
                    :disabled="true"
                    autocomplete
                  ></ion-input>
                </div>
              </div>
            </div>

            <!-- SAML Configuration -->
            <div v-if="selectedProvider === 'saml'" class="form-section">
              <h3>SAML Configuration</h3>

              <div class="form-row">
                <div class="form-field">
                  <label>SSO URL *</label>
                  <ion-input
                    v-model="providerConfig.saml.ssoUrl"
                    placeholder="https://your-provider.com/saml/sso"
                    required
                    autocomplete
                  ></ion-input>
                  <small
                    >The SAML single sign-on URL from your identity
                    provider</small
                  >
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>Entity ID *</label>
                  <ion-input
                    v-model="providerConfig.saml.entityId"
                    placeholder="https://your-provider.com/saml/metadata"
                    required
                    autocomplete="on"
                  ></ion-input>
                  <small>The SAML entity ID from your identity provider</small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>Service Provider Entity ID *</label>
                  <ion-input
                    v-model="providerConfig.saml.rp_entity_id"
                    placeholder="xyz"
                    required
                    autocomplete
                  ></ion-input>
                </div>
              </div>

              <div class="form-row">
                <div class="form-field full-width">
                  <label>X.509 Certificate *</label>
                  <ion-textarea
                    v-model="providerConfig.saml.certificate"
                    placeholder="-----BEGIN CERTIFICATE-----&#10;MIIDBzCCAe+gAwIBAgIJAKZKkZQQhYdTMA0GCSqGSIb3DQEBCwUAMCExHzAdBgNV&#10;...&#10;-----END CERTIFICATE-----"
                    rows="8"
                    required
                    class="certificate-field"
                  ></ion-textarea>
                  <small
                    >The public certificate from your SAML identity
                    provider</small
                  >
                </div>
              </div>
            </div>

            <!-- OIDC Configuration -->
            <div v-if="selectedProvider === 'oidc'" class="form-section">
              <h3>OpenID Connect Configuration</h3>

              <div class="form-row">
                <div class="form-field">
                  <label>Client ID *</label>
                  <ion-input
                    v-model="providerConfig.oidc.clientId"
                    placeholder="your-client-id"
                    required
                    autocomplete
                  ></ion-input>
                  <small>The client ID from your OIDC provider</small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>Client Secret *</label>
                  <ion-input
                    v-model="providerConfig.oidc.clientSecret"
                    placeholder="your-client-secret"
                    required
                    autocomplete
                  ></ion-input>
                  <small>The client secret from your OIDC provider</small>
                </div>
              </div>

              <div class="form-row">
                <div class="form-field">
                  <label>Issuer URL *</label>
                  <ion-input
                    v-model="providerConfig.oidc.issuerUrl"
                    placeholder="https://your-provider.com/.well-known/openid_configuration"
                    required
                    autocomplete
                  ></ion-input>
                  <small>The issuer URL from your OIDC provider</small>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="form-actions">
              <ion-button
                expand="block"
                type="submit"
                :disabled="!isFormValid || loading"
                class="primary-button"
              >
                <ion-spinner
                  v-if="loading"
                  name="crescent"
                  slot="start"
                ></ion-spinner>
                {{ editingProviderId ? "Update" : "Create" }} Provider
              </ion-button>

              <ion-button
                v-if="providerExists"
                fill="outline"
                color="danger"
                @click="confirmDeleteProvider"
                :disabled="loading"
              >
                Delete Provider
              </ion-button>

              <ion-button
                fill="outline"
                color="medium"
                @click="cancelProviderForm"
                :disabled="loading"
              >
                Cancel
              </ion-button>
            </div>
          </form>
        </div>
      </div>
    </ion-content>
  </master-layout>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useUIStore } from '@/stores/ui';
import { useRouter } from "vue-router";
import {
  IonContent,
  IonLoading,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonInput,
  IonTextarea,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  toastController,
  alertController,
} from "@ionic/vue";
import { useAuthStore } from '@/stores/auth';
import apiInterceptor from "@/shared/apiInterceptor";
import { environment } from "@/shared/env";
import { queryClient } from "@/shared/queryClient";

// Props
const props = defineProps({
  providerId: {
    type: String,
    default: null,
  },
});

const router = useRouter();
const _authStore = useAuthStore();
const token = computed(() => _authStore.authData.token);
const updateLoading = (payload) => useUIStore().updateLoading(payload);

// Reactive state
const loadingTenants = ref(false);
const selectedProvider = ref("");
const editingProviderId = ref(props.providerId);
const addNewTenant = ref(false);
const tenantName = ref("");
const tenantId = ref(null);
const tenants = ref([]);
const selectedTenant = ref(null);
const providerExists = ref(false);
const providerFetchError = ref(false);
const showProviderForm = ref(false);

// Provider configuration
const providerConfig = ref({
  name: "",
  providerId: "",
  // SAML config
  saml: {
    ssoUrl: "",
    entityId: "",
    certificate: "",
    emailAttribute:
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
    displayNameAttribute:
      "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name",
    signRequests: false,
    rp_entity_id: "",
  },
  // OIDC config
  oidc: {
    clientId: "",
    clientSecret: "",
    issuerUrl: "",
    scopes: "openid profile email",
    additionalParams: "",
  },
});

// Computed properties
const isFormValid = computed(() => {
  if (
    !selectedProvider.value ||
    !providerConfig.value.name ||
    !providerConfig.value.providerId
  ) {
    return false;
  }

  if (selectedProvider.value === "saml") {
    return (
      providerConfig.value.saml.ssoUrl &&
      providerConfig.value.saml.entityId &&
      providerConfig.value.saml.certificate
    );
  }

  if (selectedProvider.value === "oidc") {
    return (
      providerConfig.value.oidc.clientId &&
      providerConfig.value.oidc.clientSecret &&
      providerConfig.value.oidc.issuerUrl
    );
  }

  return false;
});

// Fetch tenants when component mounts
const fetchTenants = async () => {
  loadingTenants.value = true;
  try {
    const response = await apiInterceptor?.get(
      environment('VUE_APP_API_URL') + "auth/sso/tenant",
      {
        headers: {
          Authorization: `Bearer ${token.value}`,
        },
      }
    );
    const tenantsData = response;
    console.log('fetchTenants response:', tenantsData);

    if (
      !tenantsData ||
      (tenantsData.status && tenantsData.status === "error") ||
      !tenantsData.tenant_id
    ) {
      console.log('Condition failed:', {
        hasData: !!tenantsData,
        status: tenantsData?.status,
        hasTenantId: !!tenantsData?.tenant_id
      });
      return;
    }

    tenants.value = [
      {
        tenant_id: tenantsData?.tenant_id ?? "",
        display_name: tenantsData?.display_name ?? "",
      },
    ];
    selectedTenant.value = tenants.value[0] || null;
  } catch (error) {
    console.error(error);
    const toast = await toastController.create({
      message: `Failed to load tenants: ${
        error.response?.data?.message || error.message
      }`,
      duration: 4000,
      color: "danger",
    });
    toast.present();
    tenants.value = [];
    selectedTenant.value = null;
  } finally {
    loadingTenants.value = false;
  }
};

// Create tenant
const createTenant = async () => {
  if (!tenantName.value) return;
  loadingTenants.value = true;
  try {
    const response = await apiInterceptor?.post(
      environment('VUE_APP_API_URL') + "auth/sso/tenant",
      {
        display_name: tenantName.value,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token.value}`,
        },
      }
    );
    const tenant = response;

    if (tenant.status === "error") {
      throw new Error(tenant.message || "Failed to create tenant");
    }

    const toast = await toastController.create({
      message: "Tenant created successfully.",
      duration: 3000,
      color: "success",
    });
    toast.present();

    await fetchTenants();
    addNewTenant.value = false;
    selectedTenant.value = {
      tenant_id: tenant.tenant_id,
      display_name: tenant.display_name,
    };
    tenantName.value = "";
  } catch (error) {
    const toast = await toastController.create({
      message: `${error.response?.data?.message || error.message}`,
      duration: 4000,
      color: "danger",
    });
    toast.present();
  } finally {
    loadingTenants.value = false;
  }
};

// Simple confirmation dialog for tenant deletion
const confirmDeleteTenant = async () => {
  const alert = await alertController.create({
    header: "Delete Tenant",
    message:
      "Are you sure you want to delete this tenant? This action cannot be undone.",
    buttons: [
      {
        text: "Cancel",
        role: "cancel",
      },
      {
        text: "Delete",
        role: "destructive",
        handler: () => deleteTenant(selectedTenant.value.tenant_id),
      },
    ],
  });
  await alert.present();
};

// Delete tenant
const deleteTenant = async (tenantId) => {
  if (!tenantId) {
    const toast = await toastController.create({
      message: "Tenant ID is required to delete.",
      duration: 3000,
      color: "warning",
    });
    toast.present();
    return;
  }

  loadingTenants.value = true;
  try {
    await apiInterceptor.delete(
      environment('VUE_APP_API_URL') + `auth/sso/tenant`,
      {
        headers: {
          Authorization: `Bearer ${token.value}`,
          "Content-Type": "application/json",
        },
        data: {},
      }
    );

    const toast = await toastController.create({
      message: "Tenant deleted successfully.",
      duration: 3000,
      color: "success",
    });
    toast.present();

    tenants.value = [];
    selectedTenant.value = null;
    await fetchTenants();
  } catch (error) {
    const toast = await toastController.create({
      message: `Error deleting tenant: ${
        error.response?.data?.message || error.message
      }`,
      duration: 3000,
      color: "danger",
    });
    toast.present();
  } finally {
    loadingTenants.value = false;
  }
};

// Simple confirmation dialog for provider deletion
const confirmDeleteProvider = async () => {
  const alert = await alertController.create({
    header: "Delete SSO Provider",
    message:
      "Are you sure you want to delete this SSO provider? This will also log out all SSO users.",
    buttons: [
      {
        text: "Cancel",
        role: "cancel",
      },
      {
        text: "Delete",
        role: "destructive",
        handler: () => deleteProvider(),
      },
    ],
  });
  await alert.present();
};

// Delete provider with user logout
const deleteProvider = async () => {
  if (!editingProviderId.value) return;
  updateLoading({ isLoading: true, message: "Deleting provider..." });

  try {
    const deleteUrl = `${environment('VUE_APP_API_URL')}auth/sso/provider/${selectedProvider.value}`;
    await apiInterceptor.delete(deleteUrl, {
      headers: {
        Authorization: `Bearer ${token.value}`,
        "Content-Type": "application/json",
      },
    });

    try {
      const logoutUrl = `${environment('VUE_APP_API_URL')}auth/logout/sso/all`;
      await apiInterceptor.post(
        logoutUrl,
        {},
        {
          headers: {
            Authorization: `Bearer ${token.value}`,
            "Content-Type": "application/json",
          },
        }
      );
    } catch (logoutError) {
      console.warn("Failed to logout all SSO sessions:", logoutError);
    }

    const toast = await toastController.create({
      message:
        "SSO provider deleted successfully! You will be logged out shortly.",
      duration: 3000,
      color: "success",
    });
    toast.present();

    setTimeout(async () => {
      try {
        // Logout the current user
        await _authStore.logoutUser();

        const logoutToast = await toastController.create({
          message:
            "You have been logged out due to SSO provider deletion. Please login again.",
          duration: 4000,
          color: "warning",
        });
        logoutToast.present();

        router.go("/login");
        window.location.reload();
      } catch (logoutError) {
        console.error("Error during logout:", logoutError);

        router.go("/login");
        window.location.reload();
      }
    }, 1500);
  } catch (error) {
    const toast = await toastController.create({
      message:
        error.response?.data?.message ||
        error.message ||
        "Error deleting provider",
      duration: 3000,
      color: "danger",
    });
    toast.present();
  } finally {
    updateLoading({ isLoading: false });
  }
};

// Generate provider ID
const generateProviderId = () => {
  if (!providerConfig.value.name || editingProviderId.value) return;

  const sanitized = providerConfig.value.name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  const prefix = selectedProvider.value === "saml" ? "saml." : "oidc.";
  providerConfig.value.providerId = `${prefix}${sanitized}`;
};

// Provider type change handler
const onProviderTypeChange = () => {
  if (providerConfig.value.name && !editingProviderId.value) {
    generateProviderId();
  }
};

// Fetch provider details
const fetchProviderDetails = async () => {
  const roleDetails = queryClient.getQueryData(['role-details']);
  const userEmail = roleDetails?.email ?? _authStore.authData.email;
  const userDomain = userEmail?.split("@")[1];

  const providerType = await getProvidersByDomain(userDomain ?? "");

  providerExists.value = false;
  providerFetchError.value = false;
  showProviderForm.value = false;
  editingProviderId.value = null;
  updateLoading({ isLoading: true, message: "Fetching provider details..." });

  try {
    let details = null;
    try {
      if (providerType?.[0]?.type === "saml") {
        details = await getSamlProviderDetails();
        selectedProvider.value = "saml";
      } else if (providerType?.[0]?.type === "oidc") {
        details = await getOidcProviderDetails();
        selectedProvider.value = "oidc";
      }
    } catch (e) {
      providerFetchError.value = true;
      showProviderForm.value = false;
      providerExists.value = false;
      editingProviderId.value = null;
      updateLoading({ isLoading: false });
      return;
    }

    if (details) {
      providerExists.value = true;
      showProviderForm.value = true;
      providerFetchError.value = false;
      editingProviderId.value = details.provider?.provider_id;

      if (selectedProvider.value === "saml") {
        providerConfig.value.name = details.provider?.display_name;
        providerConfig.value.providerId = details.provider?.provider_id;
        providerConfig.value.saml.ssoUrl = details.provider?.sso_url;
        providerConfig.value.saml.entityId = details.provider?.idp_entity_id;
        providerConfig.value.saml.rp_entity_id =
          details.provider?.rp_entity_id || "";
        providerConfig.value.saml.certificate =
          details.provider?.x509_certificates?.[0] || "";
      } else if (selectedProvider.value === "oidc") {
        providerConfig.value.name = details.provider?.display_name;
        providerConfig.value.providerId = details.provider?.provider_id;
        providerConfig.value.oidc.clientId = details.provider?.client_id;
        providerConfig.value.oidc.clientSecret =
          details.provider?.client_secret;
        providerConfig.value.oidc.issuerUrl = details.provider?.issuer;
      }
    }
  } catch (err) {
    providerFetchError.value = true;
    showProviderForm.value = false;
    providerExists.value = false;
    editingProviderId.value = null;
  } finally {
    updateLoading({ isLoading: false });
  }
};

// API helper functions
const getProvidersByDomain = async (domain) => {
  try {
    const response = await apiInterceptor.get(
      environment('VUE_APP_API_URL') + `auth/sso/providers/domain/${domain}`
    );
    return response;
  } catch (error) {
    console.error("Error fetching providers by domain:", error);
    throw error;
  }
};

const getSamlProviderDetails = async () => {
  try {
    const response = await apiInterceptor.get(
      environment('VUE_APP_API_URL') + "auth/sso/provider/saml",
      {
        headers: {
          Authorization: `Bearer ${token.value}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching SAML provider details:", error);
    throw error;
  }
};

const getOidcProviderDetails = async () => {
  try {
    const response = await apiInterceptor?.get(
      environment('VUE_APP_API_URL') + "auth/sso/provider/oidc",
      {
        headers: {
          Authorization: `Bearer ${token.value}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error fetching OIDC provider details:", error);
    throw error;
  }
};

// Save provider
const saveProvider = async () => {
  updateLoading({ isLoading: true, message: editingProviderId.value ? "Updating provider..." : "Creating provider..." });
  try {
    let endpoint = "";
    let payload = {};
    let method = "POST";

    if (providerExists.value && editingProviderId.value) {
      method = "PUT";
    }

    if (selectedProvider.value === "saml") {
      endpoint = "auth/sso/provider/saml";
      payload = {
        display_name: providerConfig.value.name,
        enabled: true,
        provider_id: providerConfig.value.providerId,
        idp_entity_id: providerConfig.value.saml.entityId,
        sso_url: providerConfig.value.saml.ssoUrl,
        x509_certificates: [providerConfig.value.saml.certificate.trim()],
        rp_entity_id: providerConfig.value.saml.rp_entity_id,
      };
    } else if (selectedProvider.value === "oidc") {
      endpoint = "auth/sso/provider/oidc";
      payload = {
        display_name: providerConfig.value.name,
        enabled: true,
        provider_id: providerConfig.value.providerId,
        client_id: providerConfig.value.oidc.clientId,
        issuer: providerConfig.value.oidc.issuerUrl,
        client_secret: providerConfig.value.oidc.clientSecret,
      };
    } else {
      throw new Error("Unsupported provider type");
    }

    const url = `${environment('VUE_APP_API_URL')}${endpoint}`;

    let response;
    if (method === "PUT") {
      response = await apiInterceptor.put(url, payload, {
        headers: {
          Authorization: `Bearer ${token.value}`,
          "Content-Type": "application/json",
        },
      });
    } else {
      response = await apiInterceptor.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token.value}`,
          "Content-Type": "application/json",
        },
      });
    }

    const toast = await toastController.create({
      message:
        method === "PUT"
          ? "Provider updated successfully!"
          : "Provider created successfully!",
      duration: 2000,
      color: "success",
    });
    toast.present();

    window.location.reload();
  } catch (error) {
    const toast = await toastController.create({
      message:
        error.response?.data?.detail ||
        error.message ||
        "Error saving provider",
      duration: 2000,
      color: "danger",
    });
    toast.present();
  } finally {
    updateLoading({ isLoading: false });
  }
};

// Navigation functions
const goBack = () => {
  router.go(-1);
};

const cancelProviderForm = () => {
  selectedProvider.value = "";
  selectedTenant.value = null;
  showProviderForm.value = false;
  editingProviderId.value = null;
};

// Watchers
watch(
  () => providerConfig.value.name,
  () => {
    if (selectedProvider.value && !editingProviderId.value) {
      generateProviderId();
    }
  }
);

watch(selectedProvider, () => {
  if (providerConfig.value.name && !editingProviderId.value) {
    generateProviderId();
  }
});

watch(selectedTenant, async (tenant) => {
  if (tenant) {
    await fetchProviderDetails();
  }
});

// Mount
onMounted(fetchTenants);
</script>

<style lang="scss" scoped src="./../../resources/css/sso-settings.scss"></style>
